import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { MatchStakeIcon } from '../components/MatchStakeLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmailSent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resending, setResending] = useState(false);

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8">
          <MatchStakeIcon className="w-12 h-12" />
          <span className="font-heading font-bold text-2xl tracking-tight">MATCHSTAKE</span>
        </div>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-[#00F0FF]" />
          </div>
          
          <h1 className="font-heading text-2xl font-bold mb-2">Check Your Email</h1>
          <p className="text-white/50 mb-6">
            We've sent a verification link to{' '}
            <span className="text-[#00F0FF]">{user?.email || 'your email'}</span>.
            Please click the link to verify your account.
          </p>

          <div className="p-4 rounded-xl bg-white/5 mb-6">
            <p className="text-sm text-white/60">
              Didn't receive the email? Check your spam folder or click below to resend.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleResendEmail}
              disabled={resending}
              variant="outline"
              className="w-full btn-secondary"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full btn-primary"
              data-testid="continue-dashboard-btn"
            >
              Continue to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <p className="text-xs text-white/40 mt-6">
            Note: You'll need to verify your email before you can create matches, join matches, or withdraw funds.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailSent;
